import { FederalState } from './models/Company'

const bundesPrefix: Record<FederalState, string> = {
  BW: '28',
  BY: '9',
  BE: '11',
  BB: '3',
  HB: '24',
  HH: '22',
  HE: '26',
  MV: '4',
  NI: '23',
  NW: '5',
  RP: '27',
  SL: '1',
  SN: '3',
  ST: '3',
  SH: '21',
  TH: '4',
}

const landesSchemaLength: Record<FederalState, number> = {
  BW: 10,
  BY: 11,
  BE: 10,
  BB: 11,
  HB: 10,
  HH: 10,
  HE: 11,
  MV: 11,
  NI: 10,
  NW: 11,
  RP: 10,
  SL: 11,
  SN: 11,
  ST: 11,
  SH: 10,
  TH: 11,
}

function toBundesfinanzamtsnummer(state: FederalState, digits: string): string {
  const normalized =
    state === 'HE' && digits.startsWith('0') ? digits.substring(1) : digits
  return `${bundesPrefix[state]}${normalized}`.substring(0, 4)
}

function isPlaceholderTaxId(digits: string): boolean {
  return /^(\d)\1+$/.test(digits) || digits.startsWith('123456789')
}

export function taxOfficeNumberCandidates(
  county: FederalState | null | undefined,
  taxId: string | null | undefined
): { primary: string | null; alternates: string[] } {
  const digits = taxId?.replace(/\D/g, '') ?? ''
  if (digits.length < 3 || isPlaceholderTaxId(digits)) {
    return { primary: null, alternates: [] }
  }

  if (digits.length === 12 || digits.length === 13) {
    return { primary: digits.substring(0, 4), alternates: [] }
  }

  const primary = county ? toBundesfinanzamtsnummer(county, digits) : null

  const alternates = (Object.keys(landesSchemaLength) as FederalState[])
    .filter(
      (state) => state !== county && landesSchemaLength[state] === digits.length
    )
    .map((state) => toBundesfinanzamtsnummer(state, digits))
    .filter((number) => number !== primary)

  return { primary, alternates: [...new Set(alternates)] }
}

export function resolveTaxOfficeNumber({
  county,
  taxId,
  override,
  exists,
}: {
  county: FederalState | null | undefined
  taxId: string | null | undefined
  override: string | null | undefined
  exists: (taxOfficeNumber: string) => boolean
}): string | null {
  const derived = deriveTaxOfficeNumber(county, taxId, exists)
  if (derived) {
    return derived
  }

  return override && exists(override) ? override : null
}

/**
 * Every office number worth looking up to resolve the Finanzamt: the derived
 * candidates plus a manual pick, which only counts when nothing derives.
 */
export function taxOfficeLookupNumbers(
  county: FederalState | null | undefined,
  taxId: string | null | undefined,
  override: string | null | undefined
): string[] {
  const { primary, alternates } = taxOfficeNumberCandidates(county, taxId)
  return [
    ...new Set(
      [primary, ...alternates, override].filter((number): number is string =>
        Boolean(number)
      )
    ),
  ]
}

export function deriveTaxOfficeNumber(
  county: FederalState | null | undefined,
  taxId: string | null | undefined,
  exists: (taxOfficeNumber: string) => boolean
): string | null {
  const { primary, alternates } = taxOfficeNumberCandidates(county, taxId)
  if (primary && exists(primary)) {
    return primary
  }

  const existingAlternates = alternates.filter(exists)
  return existingAlternates.length === 1 ? existingAlternates[0] : null
}
